if (!customElements.get('products-toolbar')) {
  class ProductsToolbar extends HTMLElement {
    constructor() {
      super();
      this.desktopDrawerTransitionMs = 300;
      // Apply inline styles IMMEDIATELY before any other code runs
      this.forceFixedPosition();
      this.init();
    }

    /**
     * Forces the toolbar to be position: fixed at bottom via inline styles.
     * Inline styles with !important beat any stylesheet rule.
     */
    forceFixedPosition() {
      // Use setAttribute so multiple properties get set atomically with !important
      const isMobile = window.matchMedia('(max-width: 1023px)').matches;
      const bottom = isMobile ? '14px' : '20px';
      const padding = isMobile ? '0 12px' : '0';
      const gap = isMobile ? '8px' : '12px';
      this.setAttribute('style', `
        position: fixed !important;
        bottom: ${bottom} !important;
        left: 0 !important;
        right: 0 !important;
        z-index: 200 !important;
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        gap: ${gap} !important;
        padding: ${padding} !important;
        margin: 0 !important;
        background: transparent !important;
        border: none !important;
        pointer-events: none !important;
        width: auto !important;
      `.replace(/\s+/g, ' ').trim());
    }

    init() {
      this.filtersComponent = document.querySelector('facet-filters');
      this.filtersColumn = document.querySelector('.main-products-grid__filters');
      this.layoutSwitcher = this.querySelector('.products-toolbar__layout');
      this.compareToggle = document.getElementById('compare-switch');
      this.sortBy = this.querySelector('.products-toolbar__sort');
      this.productsGrid = document.querySelector('.main-products-grid');

      // Re-apply fixed position on resize (in case breakpoint changes)
      window.addEventListener('resize', () => this.forceFixedPosition(), { passive: true });

      if (this.filtersColumn) {
        this.filterToggle = this.querySelector('.js-toggle-filters');
        if (this.filterToggle) {
          this.filterToggle.addEventListener('click', this.handleFilterToggle.bind(this));
        }

        // Desktop: clicking the dim backdrop closes the filter
        this.filtersColumn.addEventListener('click', (e) => {
          if (theme.mediaMatches.lg && this.filtersOpen && e.target === this.filtersColumn) {
            this.toggleFilters();
          }
        });

        // X / footer close button: clear sort mode and close on desktop
        document.querySelectorAll('.js-close-drawer').forEach((btn) => {
          btn.addEventListener('click', (evt) => {
            const isFilterCloseButton = this.filtersComponent && btn.closest('facet-filters') === this.filtersComponent;
            if (this.filtersComponent) this.filtersComponent.classList.remove('is-sort-mode');
            this.setScrollLock(false);
            if (isFilterCloseButton && !theme.mediaMatches.lg) {
              evt.stopPropagation();
              this.filtersComponent.close();
            }
            if (theme.mediaMatches.lg && this.filtersOpen) {
              this.closeDesktopFilters();
            }
          });
        });
      }

      // Sortering button: custom-select.js opens dropdown on mousedown, so intercept mousedown + click
      if (this.filtersComponent && this.sortBy) {
        this.sortBy.addEventListener('mousedown', (e) => {
          e.stopImmediatePropagation();
          e.preventDefault();
        }, true);

        this.sortBy.addEventListener('click', (e) => {
          e.stopImmediatePropagation();
          e.preventDefault();
          this.filtersComponent.classList.add('is-sort-mode');
          if (theme.mediaMatches.lg && this.filtersColumn) {
            if (!this.filtersOpen) this.toggleFilters();
          } else {
            this.filtersComponent.open();
            this.setScrollLock(true);
          }
        }, true);
      }

      // Track drawer open/close to manage scroll lock
      if (this.filtersComponent) {
        const observer = new MutationObserver(() => {
          const drawerOpen = this.filtersComponent.getAttribute('aria-hidden') === 'false';
          // Only lock scroll on mobile — on desktop we handle it in toggleFilters
          if (!theme.mediaMatches.lg) {
            this.setScrollLock(drawerOpen);
          }
        });
        observer.observe(this.filtersComponent, { attributes: true, attributeFilter: ['aria-hidden'] });
      }

      if (this.layoutSwitcher) {
        this.layoutSwitcher.addEventListener('change', this.handleLayoutChange.bind(this));

        const layout = theme.storageUtil.get('products-layout');
        if (layout && layout === 'list') {
          const toggle = document.getElementById(`${layout}-layout-opt`);
          if (toggle) toggle.click();
          this.handleLayoutChange(null, layout);
        }
      }

      // Compare toggle — guarded against null (this theme doesn't use compare)
      if (this.compareToggle) {
        this.compareToggle.addEventListener('change', this.toggleCompare.bind(this));
        const isChecked = theme.storageUtil.get('compare-toggle', true);
        if (isChecked !== null) {
          this.toggleCompare(null, isChecked, true);
        } else {
          this.toggleCompare(null, this.compareToggle.checked, false);
        }
        setTimeout(() => {
          this.classList.add('transition-ready');
          if (this.productsGrid) this.productsGrid.classList.add('transition-ready');
        }, 500);
      } else if (theme.settings && theme.settings.compareToggle === 'none') {
        this.toggleCompare(null, true, false);
      }

      if (!this.filtersComponent && this.sortBy) {
        this.sortBy.addEventListener('change', ProductsToolbar.handleSortByChange);
      }
    }

    /**
     * Locks/unlocks body scroll when drawer opens/closes.
     */
    setScrollLock(locked) {
      document.documentElement.classList.toggle('drawer-open', locked);
    }

    /**
     * Updates the UI to reflect the compare state
     */
    toggleCompare(evt, checked = null, persist = true) {
      const isChecked = evt ? evt.target.checked : checked;
      if (this.productsGrid) this.productsGrid.dataset.compare = isChecked;

      const compareButton = document.getElementById('compare-drawer-open');
      if (compareButton) compareButton.classList.toggle('is-out', !isChecked);

      if (!evt && this.compareToggle) this.compareToggle.checked = isChecked;

      if (persist && !Shopify.designMode) {
        theme.storageUtil.set('compare-toggle', isChecked);
      }

      if (isChecked) {
        const resourcesTag = document.getElementById('compare-resources');
        if (resourcesTag) {
          const resources = JSON.parse(resourcesTag.textContent);
          resources.forEach((resource) => {
            if (resource.type === 'js') {
              const script = document.createElement('script');
              script.src = resource.path;
              document.head.appendChild(script);
            } else if (resource.type === 'css') {
              const link = document.createElement('link');
              link.href = resource.path;
              link.type = 'text/css';
              link.rel = 'stylesheet';
              document.head.appendChild(link);
            }
          });
          resourcesTag.remove();
        }
      }
    }

    /**
     * Opens the filter drawer in filter mode instead of leaving it in sort mode.
     */
    handleFilterToggle() {
      const wasSortMode = this.filtersComponent && this.filtersComponent.classList.contains('is-sort-mode');

      if (this.filtersComponent) {
        this.filtersComponent.classList.remove('is-sort-mode');
      }

      // If the shared drawer is already open in sort mode, switch content in place.
      if (wasSortMode) {
        if (theme.mediaMatches.lg) {
          if (!this.filtersOpen) this.toggleFilters();
        } else {
          this.filtersComponent.open();
          this.setScrollLock(true);
        }
        return;
      }

      this.toggleFilters();
    }

    /**
     * Toggles open/closed state of the filters on desktop.
     */
    toggleFilters() {
      this.filterToggle.classList.toggle('is-active');
      this.filtersOpen = this.filterToggle.classList.contains('is-active');
      this.filterToggle.setAttribute('aria-expanded', this.filtersOpen);

      if (theme.mediaMatches.lg) {
        if (this.filtersOpen) {
          this.openDesktopFilters();
        } else {
          this.closeDesktopFilters();
        }
      } else {
        this.filtersComponent.classList.remove('is-sort-mode');
        this.filtersComponent.open();
        this.setScrollLock(true);
      }
    }

    openDesktopFilters() {
      clearTimeout(this.desktopCloseTimer);
      this.filtersColumn.classList.remove('lg:js-hidden');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.filtersColumn.classList.add('is-visible');
        });
      });
      this.setScrollLock(true);
    }

    closeDesktopFilters() {
      clearTimeout(this.desktopCloseTimer);
      this.filtersOpen = false;
      if (this.filterToggle) {
        this.filterToggle.classList.remove('is-active');
        this.filterToggle.setAttribute('aria-expanded', 'false');
      }
      this.filtersColumn.classList.remove('is-visible');
      this.setScrollLock(false);
      this.desktopCloseTimer = setTimeout(() => {
        this.filtersColumn.classList.add('lg:js-hidden');
      }, this.desktopDrawerTransitionMs);
    }

    handleLayoutChange(evt, layout) {
      if (this.productsGrid) this.productsGrid.dataset.layout = layout || evt.target.value;
      if (!layout) theme.storageUtil.set('products-layout', evt.target.value);
    }

    static handleSortByChange(evt) {
      const urlObj = new URL(window.location.href);
      urlObj.searchParams.set('sort_by', evt.detail.selectedValue);
      urlObj.hash = 'products-toolbar';
      window.location.href = urlObj.toString();
    }
  }

  customElements.define('products-toolbar', ProductsToolbar);
}
